// ─── ContactUsModal — in-app contact form ────────────────────────────────────
// Opens from the user dropdown menu. Posts to /api/contact which forwards
// the message to the support inbox. Falls back to a mailto: link if the
// API fails (e.g. when SUPPORT_EMAIL isn't configured yet).
//
// The "send" action is fully wired even when no real support email is
// configured — the API endpoint accepts and queues the request; ops can
// review queued rows in the database until a forwarder is hooked up.

import { useState, useEffect } from "react";
import { useTheme } from "../lib/ThemeContext.jsx";
import { FONT, RADIUS, SPRING } from "../lib/theme.js";
import { supabase } from "../hooks/useData.js";

const TOPICS = [
  { id: "question",  label: "💬 Ask a question" },
  { id: "bug",       label: "🐛 Report a bug" },
  { id: "feature",   label: "💡 Request a feature" },
  { id: "billing",   label: "💳 Billing & subscription" },
  { id: "other",     label: "📨 Something else" },
];

export default function ContactUsModal({ open, onClose, session, account }) {
  const T = useTheme();
  const [topic, setTopic]     = useState("question");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus]   = useState(""); // "" | "sending" | "sent" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  // Reset state every time the modal opens
  useEffect(() => {
    if (open) {
      setStatus(""); setErrorMsg("");
    }
  }, [open]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    function handle(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", handle);
    return () => document.removeEventListener("keydown", handle);
  }, [open, onClose]);

  if (!open) return null;

  const fromEmail = session?.user?.email || "";
  const accountName = account?.name || account?.brand_export_name || "";
  const planTier = account?.plan_tier || "starter";

  async function handleSend() {
    const trimmedSubject = subject.trim().slice(0, 200);
    const trimmedMessage = message.trim().slice(0, 5000);
    if (trimmedMessage.length < 10) {
      setErrorMsg("Please tell us a bit more — at least 10 characters.");
      return;
    }
    setStatus("sending"); setErrorMsg("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          topic,
          subject: trimmedSubject,
          message: trimmedMessage,
          // Server uses the authed user's email by default; we also send it
          // here so anonymous (if ever supported) submissions still have a from.
          fromEmail,
          accountName,
          planTier,
          userAgent: navigator?.userAgent || "",
          page: window?.location?.hash || "",
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || `Send failed (${res.status})`);
      }
      setStatus("sent");
      setSubject(""); setMessage("");
    } catch (e) {
      setStatus("error");
      setErrorMsg(e?.message || "Something went wrong. Please try again.");
    }
  }

  // Mailto fallback — opens the user's email client with everything pre-filled.
  // Useful while SUPPORT_EMAIL isn't configured server-side.
  function buildMailto() {
    const to = "support@cashhorizon.io"; // ops can change later
    const subj = `[CashHorizon · ${TOPICS.find(t => t.id === topic)?.label.replace(/^\W+\s*/, "") || "Question"}] ${subject || "—"}`;
    const body = [
      `From: ${fromEmail || "(no email)"}`,
      accountName ? `Account: ${accountName}` : null,
      `Plan: ${planTier}`,
      "",
      message || "",
    ].filter(Boolean).join("\n");
    return `mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(subj)}&body=${encodeURIComponent(body)}`;
  }

  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, background:"rgba(5,7,15,0.65)",
      display:"flex", alignItems:"center", justifyContent:"center", zIndex:9999,
      padding:"20px", animation:"sfFadeIn 0.18s ease both", backdropFilter:"blur(4px)",
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background:T.card, border:`1px solid ${T.border}`, borderRadius:RADIUS.lg,
        boxShadow:"0 24px 64px rgba(0,0,0,0.5)", width:"100%", maxWidth:520,
        padding:"24px 26px", fontFamily:FONT, position:"relative",
      }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:14, marginBottom:6 }}>
          <div>
            <div style={{ fontSize:18, fontWeight:800, color:T.text, letterSpacing:-0.3 }}>✉️ Contact us</div>
            <div style={{ fontSize:12, color:T.textSub, marginTop:3 }}>We read every message — usually reply within 24 hours.</div>
          </div>
          <button onClick={onClose} aria-label="Close" style={{
            background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
            color:T.textSub, fontSize:14, padding:"4px 10px", cursor:"pointer", fontFamily:FONT,
          }}>✕</button>
        </div>

        {/* Status — sent */}
        {status === "sent" && (
          <div style={{
            marginTop:18, padding:"22px 18px", borderRadius:RADIUS.md,
            background:T.green+"12", border:`1px solid ${T.green}50`, textAlign:"center",
          }}>
            <div style={{ fontSize:32, marginBottom:6 }}>✓</div>
            <div style={{ fontSize:15, fontWeight:700, color:T.green, marginBottom:4 }}>Message sent</div>
            <div style={{ fontSize:12, color:T.textSub, lineHeight:1.5 }}>
              Thanks — we'll get back to you at <strong style={{ color:T.text }}>{fromEmail || "your account email"}</strong>.
            </div>
            <button onClick={onClose} style={{
              marginTop:14, background:T.green, color:"#fff", border:"none", borderRadius:RADIUS.sm,
              padding:"9px 22px", fontSize:13, fontWeight:700, cursor:"pointer", fontFamily:FONT,
            }}>Done</button>
          </div>
        )}

        {/* Form */}
        {status !== "sent" && (
          <>
            {/* Topic chips */}
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, margin:"14px 0 12px" }}>
              {TOPICS.map(t => {
                const active = topic === t.id;
                return (
                  <button key={t.id} onClick={() => setTopic(t.id)} style={{
                    padding:"6px 12px", borderRadius:RADIUS.pill,
                    background: active ? T.accent+"20" : "transparent",
                    border:`1px solid ${active ? T.accent+"60" : T.border}`,
                    color: active ? T.accent : T.textSub,
                    fontSize:12, fontWeight: active ? 700 : 500, fontFamily:FONT, cursor:"pointer",
                    transition:`all 0.15s ${SPRING}`,
                  }}>{t.label}</button>
                );
              })}
            </div>

            {/* Subject */}
            <input type="text" placeholder="Subject (optional)" maxLength={200}
              value={subject} onChange={e => setSubject(e.target.value)}
              style={{
                width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
                color:T.text, padding:"10px 12px", fontSize:13, fontFamily:FONT, outline:"none",
                marginBottom:10, boxSizing:"border-box",
              }}/>

            {/* Message */}
            <textarea placeholder="What can we help with?" maxLength={5000} rows={6}
              value={message} onChange={e => setMessage(e.target.value)}
              style={{
                width:"100%", background:T.surface, border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
                color:T.text, padding:"10px 12px", fontSize:13, fontFamily:FONT, outline:"none",
                resize:"vertical", minHeight:120, lineHeight:1.5, boxSizing:"border-box",
              }}/>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", margin:"4px 2px 14px" }}>
              <span style={{ fontSize:10, color:T.textMuted }}>
                {fromEmail ? `Replies will go to ${fromEmail}` : "Sign in to receive a reply"}
              </span>
              <span style={{ fontSize:10, color:T.textMuted, fontVariantNumeric:"tabular-nums" }}>{message.length}/5000</span>
            </div>

            {/* Error */}
            {(status === "error" || errorMsg) && (
              <div style={{
                background:T.red+"12", border:`1px solid ${T.red}40`, borderRadius:RADIUS.sm,
                padding:"10px 12px", fontSize:12, color:T.red, marginBottom:12, lineHeight:1.5,
              }}>
                {errorMsg || "Couldn't send via the form."}
                {" "}
                <a href={buildMailto()} style={{ color:T.red, textDecoration:"underline", fontWeight:700 }}>
                  Open your email client instead →
                </a>
              </div>
            )}

            {/* Footer actions */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
              <a href={buildMailto()} title="Use your email client instead" style={{
                fontSize:11, color:T.textSub, textDecoration:"underline", cursor:"pointer",
              }}>Prefer email? Open your client</a>
              <div style={{ display:"flex", gap:8 }}>
                <button onClick={onClose} style={{
                  background:"transparent", border:`1px solid ${T.border}`, borderRadius:RADIUS.sm,
                  color:T.textSub, padding:"9px 16px", fontSize:13, cursor:"pointer", fontFamily:FONT,
                }}>Cancel</button>
                <button onClick={handleSend} disabled={status === "sending"} style={{
                  background:T.accent, color:"#fff", border:"none", borderRadius:RADIUS.sm,
                  padding:"9px 22px", fontSize:13, fontWeight:700,
                  cursor:status === "sending" ? "wait" : "pointer", fontFamily:FONT,
                  opacity:status === "sending" ? 0.7 : 1,
                }}>{status === "sending" ? "Sending…" : "Send message"}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
